import { Box, Theme, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import { invitePath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React, { useEffect } from "react";
import { useLocation } from "react-router";

export default function ReferralSignupBanner() {
  const { currentUser } = useSession();
  const isEligibleForOutreachProgram = currentUser?.teacher_approved_at;

  const { t } = useTranslation(
    "client\\components\\banners\\ReferralSignupBanner",
  );
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();

  // Track if banner view has been tracked for this mount to prevent duplicate tracking
  const hasTrackedViewRef = React.useRef<boolean>(false);
  const location = useLocation();

  const shouldShowBanner = React.useMemo(() => {
    // Wait for CU to load so we don't show wrong copy initially
    if (!currentUser) {
      return false;
    }
    // Banner-specific rule: Don't show on invite page (where users would land after clicking)
    if (location.pathname === invitePath()) {
      return false;
    }

    return true;
  }, [location.pathname, currentUser]);

  useEffect(() => {
    if (!hasTrackedViewRef.current && shouldShowBanner) {
      hasTrackedViewRef.current = true;
      trackEvent("referral_signup_banner_view", {
        is_mobile: isMobile,
      });
    }
  }, [trackEvent, isMobile, shouldShowBanner]);

  const handleBannerClick = () => {
    trackEvent("referral_signup_banner_click", {
      is_mobile: isMobile,
    });
    navigate(invitePath());
  };

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary[600],
        minHeight: "50px",
        cursor: "pointer",
        padding: "0 1em",
        [theme.breakpoints.down("md")]: {
          padding: "20px",
        },
      })}
      onClick={handleBannerClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1440px",
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              {isEligibleForOutreachProgram ? (
                <Trans t={t}>
                  <b>Earn $75 per referral.</b> Give families 50% off their
                  first 3 months and get rewarded.
                </Trans>
              ) : (
                <Trans t={t}>
                  <b>Give $50, Get $50.</b> Our best referral offer yet! $50 for
                  you, $50 for a friend.
                </Trans>
              )}
              <Box
                component="span"
                sx={{
                  marginLeft: 8,
                  textDecoration: "underline",
                }}
              >
                {t("Share today!")}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
